import { applyMiddleware, compose, createStore, Store } from 'redux';
import { combineEpics, createEpicMiddleware } from 'redux-observable';
import { BehaviorSubject, Observable, Subscriber } from 'rxjs';
import { distinctUntilChanged, mergeMap } from 'rxjs/operators';

import epics from './store.epic';
import { createReducerManager } from './store.model';
import reducers from './store.reducer';

// eslint-disable-next-line @typescript-eslint/dot-notation
const composeEnhancers = window['__REDUX_DEVTOOLS_EXTENSION_COMPOSE__']
    ? // eslint-disable-next-line @typescript-eslint/dot-notation
      window['__REDUX_DEVTOOLS_EXTENSION_COMPOSE__']({ shouldHotReload: false })
    : compose;

export interface Dependencies {
    injector?: {
        get: <T>(token: any) => T;
    };
}

export const epicDependencies: Dependencies = {};
const epicMiddleware = createEpicMiddleware({ dependencies: epicDependencies });
const epic = combineEpics(...epics);
export const epicObs = new BehaviorSubject(epic);
export const rootEpic = (action, state, deps): any =>
    epicObs.pipe(mergeMap((e) => e(action, state, deps)));

const reducerManager = createReducerManager(reducers);
const enhancer = composeEnhancers(applyMiddleware(epicMiddleware));
const store = createStore(reducerManager.reduce, enhancer);
(store as any).reducerManager = reducerManager;

epicMiddleware.run(rootEpic);

const runMapper = <T, U = T>(
    store1: Store,
    mapper: (state: T) => U,
    observer: Subscriber<U>,
): void => {
    const state = store1.getState();
    observer.next(!mapper ? state : mapper(state));
};

export const storeObserver = <T, U = T>(
    store1: Store,
    mapper?: (state: T) => U,
): Observable<U> =>
    new Observable<U>((observer) => {
        const unsubscribe = store1.subscribe(() =>
            runMapper(store1, mapper, observer),
        );
        runMapper(store1, mapper, observer);
        return unsubscribe;
    }).pipe(distinctUntilChanged());

export interface StateToProps<T> {
    state: T; // TODO revise. maybe remove this

    stateToProps?: (prevState?: T) => void;

    propsToDispatch?: () => void;
}

export default store;
